import { gql } from '@apollo/client';
import classNames from 'classnames';
import React, { useState } from 'react';
import type { EntityRow_ContributorFragment } from '../graphql/generated/graphqlTypes';
import NestableLink from '../utils/NestableLink';
import { Link } from '../utils/router/v3Bridge';
import AvatarStack from './AvatarStack';
import styles from './EntityRow.module.scss';
import HoverPreviewable from './HoverPreviewable';
import SVGIcon from './SVGIcon';
import OverflowMenu from './OverflowMenu';
import Button from './Button';
import pinIcon from '../icons/prefilled/pinOutlineWithStroke.svg';
import overflowMenuIcon from '../icons/overflowMenu.svg';

// 10 words using 14px font => 544px + horizontal padding + 2px border
const MAX_PREVIEW_WIDTH = 594;

const ENTITY_ROW_CONTRIBUTOR_FRAGMENT = gql`
  fragment EntityRow_contributor on User {
    id
    ...AvatarStack_users
  }
  ${AvatarStack.fragments.users}
`;

interface EntityRowInnerProps {
  pinned?: boolean;
  aboveTitle?: React.ReactNode;
  icon: React.ReactNode;
  meta: React.ReactNode;
  title: React.ReactNode;
  contributors?: EntityRow_ContributorFragment[];
  renderOverflowMenu?: (close: () => void) => React.ReactNode;
}

const EntityRowInner = ({
  pinned,
  aboveTitle,
  icon,
  title,
  meta,
  contributors = [],
  renderOverflowMenu,
}: EntityRowInnerProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  return (
    <div className={styles.container}>
      <div className={styles.iconContainer}>
        {pinned ? (
          <>
            {icon}
            <div className={styles.pinContainer}>
              <SVGIcon size="small">{pinIcon}</SVGIcon>
            </div>
          </>
        ) : (
          icon
        )}
      </div>
      <div className={styles.titleContainer}>
        {!!aboveTitle && <div className={styles.aboveTitle}>{aboveTitle}</div>}
        <span className={styles.title}>{title}</span>
      </div>
      <div className={styles.contributors}>
        <AvatarStack
          link
          users={contributors.slice(0, 3)}
          showHoverCard
          longHoverDelay
          noWrap
        />
      </div>
      <div className={styles.meta}>{meta}</div>

      {renderOverflowMenu && (
        <div
          className={classNames(styles.overflowMenu, {
            [styles.isOpen]: isOverflowMenuOpen,
          })}
        >
          <OverflowMenu
            preventCloseOnContentClick
            button={
              <Button className={styles.overflowMenuButton}>
                <SVGIcon coloringWith="fill">{overflowMenuIcon}</SVGIcon>
              </Button>
            }
            onOpenChange={setIsOverflowMenuOpen}
            open={isOverflowMenuOpen}
          >
            <div className={styles.overflowMenuContent}>
              {renderOverflowMenu(() => setIsOverflowMenuOpen(false))}
            </div>
          </OverflowMenu>
        </div>
      )}
    </div>
  );
};

type EntityRowPreview = {
  id: string;
  node: React.ReactNode;
  onActivePreviewIdChange: (id: string | null) => void;
  activePreviewId: string | null;
};

interface EntityRowPreviewableProps {
  preview: EntityRowPreview;
  children: React.ComponentProps<typeof HoverPreviewable>['children'];
}

const EntityRowPreviewable = ({
  preview,
  children,
}: EntityRowPreviewableProps) => (
  <HoverPreviewable
    id={preview.id}
    preview={preview.node}
    activeId={preview.activePreviewId}
    onActiveIdChange={preview.onActivePreviewIdChange}
    placement="right"
    previewWidth={MAX_PREVIEW_WIDTH}
  >
    {children}
  </HoverPreviewable>
);

interface EntityRowContainerProps
  extends Omit<EntityRowInnerProps, 'linkProps'> {
  inList: boolean;
  path: string;
  preview?: {
    id: string;
    node: React.ReactNode;
    onActivePreviewIdChange: (id: string | null) => void;
    activePreviewId: string | null;
  };
}

const EntityRow = ({
  inList,
  preview,
  path,
  ...innerProps
}: EntityRowContainerProps) => {
  return (
    <NestableLink
      to={path}
      className={classNames(styles.link, { [styles.inList]: inList })}
    >
      {preview ? (
        <EntityRowPreviewable preview={preview}>
          {(targetProps) => (
            <EntityRowInner
              {...innerProps}
              icon={
                <>
                  {innerProps.icon}
                  <Link
                    to={path}
                    className={styles.hoverTarget}
                    {...targetProps}
                  />
                </>
              }
            />
          )}
        </EntityRowPreviewable>
      ) : (
        <EntityRowInner {...innerProps} />
      )}
    </NestableLink>
  );
};

EntityRow.fragments = {
  contributor: ENTITY_ROW_CONTRIBUTOR_FRAGMENT,
};

export default EntityRow;
